import {syntaxTree} from '@codemirror/language'
import {type Extension, RangeSetBuilder} from '@codemirror/state'
import {Decoration, type DecorationSet, EditorView, ViewPlugin, type ViewUpdate} from '@codemirror/view'

import styles from './active-block.module.css'

/**
 * Builds inline decorations using the syntax tree instead of regex
 */
function buildInlineDecorations(view: EditorView): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>()
  const tree = syntaxTree(view.state)

  // Only process visible ranges for performance
  for (const {from, to} of view.visibleRanges) {
    tree.iterate({
      from,
      to,
      enter: node => {
        if (node.from < node.to) {
          const name = node.name
          const textStart = node.from
          const textEnd = node.to

          switch (name) {
            case 'StrongEmphasis':
              builder.add(textStart, textEnd, Decoration.mark({tagName: 'strong'}))
              break
            case 'Emphasis':
              builder.add(textStart, textEnd, Decoration.mark({tagName: 'em'}))
              break
            case 'EmphasisMark':
              builder.add(node.from, node.to, Decoration.mark({class: styles.delimiter}))
              break
          }
        }
      },
    })
  }

  return builder.finish()
}

/**
 * View plugin that provides inline decorations using syntax tree
 */
const inlineDecorationPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet

    constructor(view: EditorView) {
      this.decorations = buildInlineDecorations(view)
    }

    update(update: ViewUpdate) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = buildInlineDecorations(update.view)
      }
    }
  },
  {
    decorations: v => v.decorations,
  },
)

/**
 * Extension that provides inline styling for markdown using syntax tree
 */
export const inlineExtension: Extension = [inlineDecorationPlugin]
